import { API_BASE_URL } from "../config/api";

export type APAgingMetrics = {
  currencyCode: string;
  currencySymbol: string;
  totalPayables: number;
  overdueAmount: number;
  convertedTotalPayables?: number | null;
  convertedOverdueAmount?: number | null;
  convertedCurrencyCode?: string | null;
  convertedCurrencySymbol?: string | null;
  dueSoon: number;
  oldestUnpaidBill: number;
};

export type APAgingBucket = {
  currencyCode: string;
  currencySymbol: string;
  range: string;
  amount: number;
  percentage: number;
  color: string;
};

export type APAgingDistributionResponse = {
  asOfDate: string;
  totalOutstandingAmount: number;
  overdueAmount: number;
  notOverdueAmount: number;
  currencyCode: string;
  currencySymbol: string;
  buckets: APAgingBucket[];
};

export type OverdueVendor = {
  vendorId: string;
  vendor: string;
  currencyCode: string;
  currencySymbol: string;
  amount: number;
  billCount: number;
  oldestAge: number;
  status: "critical" | "warning";
};

export type DueSoonBill = {
  vendorId: string;
  vendor: string;
  vendorCode: string;
  amount: number;
  currencyCode: string;
  currencySymbol: string;
  dueDate: string;
  age: number;
};

export type VendorAdvance = {
  vendor: string;
  vendorCode: string;
  category: string;
  advancePaid: number;
  paymentDate: string;
  daysOutstanding: number;
  expectedBillAmount: number | null;
  status: string;
  recommendedAction: string;
};

export type APInsight = {
  type: "critical" | "warning" | "success" | "brand";
  title: string;
  message: string;
  badge: string;
};


function qs(params: Record<string, any>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  if (!entries.length) return "";
  const search = new URLSearchParams(entries as any).toString();
  return `?${search}`;
}

// Helper to create headers with authentication
const createHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // Add auth token if available
  const token = localStorage.getItem("token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

export async function getMetrics(
  params: { companyId?: number; startDate?: string; endDate?: string } = {}
): Promise<APAgingMetrics[]> {
  const url = `${API_BASE_URL}/api/ap-aging/metrics${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.error || `Failed to fetch AP aging metrics`
    );
  }
  return (await res.json()) as APAgingMetrics[];
}

export async function getDistribution(
  params: { companyId?: number; startDate?: string; endDate?: string } = {}
): Promise<APAgingDistributionResponse> {
  const url = `${API_BASE_URL}/api/ap-aging/distribution${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.error ||
        `Failed to fetch AP aging distribution`
    );
  }
  return (await res.json()) as APAgingDistributionResponse;
}

export type PaginatedResponse<T> = {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
};

export async function getOverdueVendors(
  params: {
    companyId?: number;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<PaginatedResponse<OverdueVendor>> {
  const url = `${API_BASE_URL}/api/ap-aging/overdue-vendors${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.error || `Failed to fetch overdue vendors`
    );
  }
  return (await res.json()) as PaginatedResponse<OverdueVendor>;
}

export async function getDueSoonBills(
  params: {
    companyId?: number;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<PaginatedResponse<DueSoonBill>> {
  const url = `${API_BASE_URL}/api/ap-aging/due-soon${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.error || `Failed to fetch due soon bills`
    );
  }
  return (await res.json()) as PaginatedResponse<DueSoonBill>;
}

export async function getVendorAdvances(
  params: { companyId?: number; startDate?: string; endDate?: string } = {}
): Promise<VendorAdvance[]> {
  const url = `${API_BASE_URL}/api/ap-aging/vendor-advances${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.error || `Failed to fetch vendor advances`
    );
  }
  return (await res.json()) as VendorAdvance[];
}

export async function getInsights(
  params: { companyId?: number } = {}
): Promise<APInsight[]> {
  const url = `${API_BASE_URL}/api/ap-aging/insights${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.error ||
        `Failed to fetch AP aging insights`
    );
  }
  return (await res.json()) as APInsight[];
}
